import { getConfig } from "./config/env";

export const APP_NAME = "Ghana Passport Online Portal";
export const REQUEST_HEADER_APP_NAME = "App-Name";

/**
 * Where an in-flight form-import job id is parked so a refresh can re-attach to a
 * parse that is still running. Shared with the logout flow, which must clear it —
 * otherwise the next user on the same browser re-attaches to the previous user's
 * import.
 */
export const IMPORT_JOB_STORAGE_KEY = "veiligh:formImportJobId";
export const DEFAULT_APP_NAME = "Veiligh-EHS";
export const BASE_URL = window.appConfig?.API_URL || getConfig().VITE_APP_BASE_URL;

export const REST_API_URL = BASE_URL;

export const RESERVED_TENANT_PREFIXES = ["localhost", "dev", "staging", "demo"];

export const buildReverseGeocodeUrl = (baseURL: string, lat: string, lon: string) => `${baseURL}/api/v1/location/reverse-geocode?latitude=${lat}&longitude=${lon}`;


export const GEOLOCATION_MESSAGES = {
  unsupported: "Geolocation is not supported by your browser.",
  success: "Current location and address successfully retrieved!",
  unresolvedAddress: "Location coordinates acquired, but could not resolve address.",
  reverseGeocodeError:
    "Location coordinates acquired, but error performing reverse geocoding.",
  permissionDenied: "User denied the request for Geolocation.",
  positionUnavailable: "Location information is unavailable.",
  timeout: "The request to get user location timed out.",
  unknownError: "An unknown error occurred while retrieving location.",
} as const;

export const routes = {
  home: () => "/hse/home",
  login: () => "/login",
  logout: () => "/logout",
  profile: () => "/profile",
  resetPassword: () => "/reset-password",
  forgotPassword: () => "/forgot-password",
  setPassword: () => "/set-password",
  otpValidation: (params: { telephone: string }) => `/otp/${params.telephone}`,
  settings: () => "/settings?tab=tabs",
  newObservation: () => "/hse/observations/create",
  newIncident: () => "/hse/incidents/create",
  incidents: () => "/hse/incidents",
  incidentDetail: ({ id }: { id: string }) => `/hse/incidents/view/${id}`,
  incidentEdit: ({ id }: { id: string }) => `/hse/incidents/edit/${id}`,

  sites: () => "/hse/sites",
  siteDetail: ({ id }: { id: string }) => `/hse/sites/${id}`,
  employees: () => "/resource/employees",
  newEmployee: () => "/resource/employees/create",
  employeeDetails: (params: { id?: string }) =>
    `/resource/employees/view/${params.id}`,
  newInspection: () => "/hse/inspections/create",
  newAudit: () => "/hse/audits/create",
  eligibilityPermits: () => "/hse/compliance/personnel/eligibility-permits",
  newEligibilityPermit: () =>
    "/hse/compliance/personnel/eligibility-permits/create",
  detailEligibilityPermit: (params: { id?: string }) =>
    `/hse/compliance/personnel/eligibility-permits/${params.id}/detail`,
  editEligibilityPermit: (params: { id?: string }) =>
    `/hse/compliance/personnel/eligibility-permits/edit/${params.id}`,
  competencePermits: () => "/hse/compliance/personnel/competence-permits",
  newCompetencePermit: () =>
    "/hse/compliance/personnel/competence-permits/create",
  detailCompetencePermit: (params: { id?: string }) =>
    `/hse/compliance/personnel/competence-permits/${params.id}/detail`,
  editCompetencePermit: (params: { id?: string }) =>
    `/hse/compliance/personnel/competence-permits/edit/${params.id}`,
  observations: () => "/hse/observations",
  observationDetail: ({ id }: { id: string }) => `/hse/observations/view/${id}`,
  observationEdit: ({ id }: { id: string }) => `/hse/observations/edit/${id}`,

  inspections: () => "/hse/inspections",
  inspectionDetail: ({ id }: { id: string }) => `/hse/inspections/view/${id}`,
  companyWidePermits: () =>
    "/hse/compliance/organizational/company-wide-permits",
  newCompanyWidePermit: () =>
    "/hse/compliance/organizational/company-wide-permits/create",
  detailCompanyWidePermit: (params: { id?: string }) =>
    `/hse/compliance/organizational/company-wide-permits/${params.id}/detail`,
  editCompanyWidePermit: (params: { id?: string }) =>
    `/hse/compliance/organizational/company-wide-permits/edit/${params.id}`,
  siteSpecificPermits: () =>
    "/hse/compliance/organizational/site-specific-permits",
  newSiteSpecificPermit: () =>
    "/hse/compliance/organizational/site-specific-permits/create",
  detailSiteSpecificPermit: (params: { id?: string }) =>
    `/hse/compliance/organizational/site-specific-permits/${params.id}/detail`,
  editSiteSpecificPermit: (params: { id?: string }) =>
    `/hse/compliance/organizational/site-specific-permits/edit/${params.id}`,
  audits: () => "/hse/audits",
  auditDetail: ({ id }: { id: string }) => `/hse/audits/view/${id}`,
  findings: () => "/hse/findings",
  findingPage: ({ id, type }: { id: string; type: string }) =>
    `/hse/${type}/${id}/findings`,
  editEmployee: (params: { id: string }) =>
    `/resource/employees/edit/${params.id}`,
  meetingEntry: (params: { id: string }) => `/hse/records/meeting/${params.id}`,
  meetingsList: () => "/hse/records/meeting",
  createMeeting: () => "/hse/records/meeting/create",
  updateMeeting: (params: { id: string }) =>
    `/hse/records/meeting/edit/${params.id}`,
  workHoursList: () => `/hse/records/work-hours`,
  workHoursDetail: (params: { id: string }) =>
    `/hse/records/work-hours/${params.id}`,

  newQuery: () => "/hse/reports/query/create",
  editQuery: (params: { id: string }) => `/hse/reports/query/edit/${params.id}`,
  manageRoles: () => "/resource/access/manage-roles",
  newRole: () => "/resource/access/manage-roles/create",
  editRole: (id: string) => `/resource/access/manage-roles/edit/${id}`,
  access: () => "/resource/access",
  environmentalRecordDetail: (params: { id: string }) =>
    `/hse/records/environmental/${params.id}`,
  environmentalRecord: () => `/hse/records/environmental`,
  healthRecord: () => "/hse/records/health",
  createEnvironmentalRecord: () => "/hse/records/environmental/create",
  editEnvironmentalRecord: (id: string) =>
    `/hse/records/environmental/edit/${id}`,
  createManHours: () => "/hse/records/work-hours/create",
  manHours: () => "/hse/records/work-hours",
  inventoryDetail: ({ id, type }: { id: string; type: InventoryType }) =>
    "/inventory/details/" + type + "/" + id,
  assets: () => "/asset/assets",
  newAsset: () => "/asset/assets/create",
  assetDetail: ({ id }: { id: string }) => `/asset/assets/view/${id}`,
  assetEdit: ({ id }: { id: string }) => `/asset/assets/edit/${id}`,
  completeAudit: ({ id }: { id: string }) => `/hse/audits/${id}/complete`,
  completeInspection: ({ id }: { id: string }) =>
    `/hse/inspections/${id}/complete`,
};

export interface Option {
  label: string;
  value: string;
}

export const RiskMatrixModuleTypes: Option[] = [
  { value: "Risk Register / Hazard", label: "Risk Register / Hazard" },
  { value: "Incident", label: "Incident" },
  { value: "Observation", label: "Observation" },
  { value: "Audit", label: "Audit" },
  { value: "Inspection (Findings)", label: "Inspection (Findings)" },
  { value: "Permit To Work", label: "Permit To Work" },
  { value: "Environmental Record", label: "Environmental Record" },
  { value: "Job Safety Analysis (JSA)", label: "Job Safety Analysis (JSA)" },
  { value: "Management Of Change (MOC)", label: "Management Of Change (MOC)" },
];

export enum TimeFrameType {
  Day = 0,
  Week = 1,
  Month = 2,
  Year = 3,
}

export enum DashboardStatus {
  Private = 0,
  Public = 1,
  Shared = 2,
}

export enum DashboardAccessLevel {
  View = 0,
  Edit = 1,
}

export enum InputTypes {
  TEXT = "text",
  TIME = "time",
  INTERNAL = "internal",
  EXTERNAL = "external",
  USER_SELECT = "user-select",
  OTP = "otp",
  EMAIL = "email",
  DOMAIN = "domain",
  NUMBER = "number",
  PASSWORD = "password",
  PHONE = "phone",
  SELECT = "select",
  MULTI = "multi",
  MOMENT = "moment",
  DATE = "date",
  DATETIME = "date-time",
  RANGE = "daterange",
  RADIO = "radio",
  CHECKBOX = "checkbox",
  TEXTAREA = "textarea",
  RICHTEXT = "richtext",
  UPLOAD = "upload",
  DRAGNDROP = "drag-n-drop",
  IMAGE = "image",
  BUTTON = "button",
  SUBMIT = "submit",
  COLOR = "color",
  SEARCHABLE_SELECT = "searchable-select",
  SPACE = "space",
  SIGNATURE = "signature",
}

export const HEADER_HEIGHT_CLASS = "h-10";

export const DEFAULT_API_PAYLOAD = {
  "App-Name": DEFAULT_APP_NAME,
  version: "1",
};

export const DEFAULT_CLIENT_ID = "web";

export const SCREENS = {
  OBSERVATION: "observations",
  INCIDENT: "incidents",
  INSPECTIONS: "inspections",
  REGULATION: "regulations",
  CERTIFICATION: "certifications",
  RENEWALS: "renewals",
  PERMITS: "permits",
  AUDITS: "audits",
  ACTIONS: "actions",
  ACTION_COMMENTS: "action-comments",
  FINDINGS: "findings",
  COSTS: "costs",
  IMPACT: "impact",
  EMPLOYEE: "employee",
  RISK: "risk",
  ENVIRONMENT_RECORD: "environment-record",
  HEALTH_ENTRY: "health-entry",
  SITES: "sites",
  DASHBOARD: "dashboard",
  ELIGIBILITY_PERMIT: "eligibility-permits",
  COMPETENCE_PERMIT: "competence-permits",
  COMPANY_WIDE_PERMIT: "company-wide-permits",
  SITE_SPECIFIC_PERMIT: "site-specific-permits",
  ELIGIBILITY_PERMIT_DOCUMENT: "eligibility-permit-documents",
  COMPETENCE_PERMIT_DOCUMENT: "competence-permit-documents",
  COMPANY_WIDE_PERMIT_DOCUMENT: "company-wide-permit-documents",
  SITE_SPECIFIC_PERMIT_DOCUMENT: "site-specific-permit-documents",
  ASSETS: "assets",
  ASSET_GROUP: "asset-group",
  ASSET_CATEGORY: "asset-category",
  MANUFACTURER: "manufacturer",
  ASSET_MODEL: "asset-model",
};
interface ModelTypeMapping {
  [key: string]: string;
}
export const MODELTYPES: ModelTypeMapping = {
  OBSERVATION: "Observation",
  INCIDENT: "Incident",
  INSPECTION: "Inspection",
  REGULATION: "Regulation",
  CERTIFICATION: "Certification",
  RENEWALS: "Renewal",
  PERMITS: "Permit",
  AUDITS: "Audit",
  ACTIONS: "Action",
  ACTION_ENTRY: "ActionEntry",
  ACTION_COMMENTS: "Action-comment",
  TRAININGS: "Training",
  FINDINGS: "Finding",
  COSTS: "Cost",
  EMPLOYEE: "Employee",
  USER: "User",
  APPROVAL: "Approval",
  ELIGIBILITY_PERMIT: "EligibilityPermit",
  COMPETENCE_PERMIT: "CompetencePermit",
  COMPANY_WIDE_PERMIT: "CompanyWidePermit",
  SITE_SPECIFIC_PERMIT: "SiteSpecificPermit",
  ELIGIBILITY_PERMIT_DOCUMENT: "EligibilityPermitDocument",
  COMPETENCE_PERMIT_DOCUMENT: "CompetencePermitDocument",
  COMPANY_WIDE_PERMIT_DOCUMENT: "CompanyWidePermitDocument",
  SITE_SPECIFIC_PERMIT_DOCUMENT: "SiteSpecificPermitDocument",
  ASSET: "Asset",
  SITE: "Site",
  MEETING_ENTRY: "MeetingEntry",
};
export const APPROVAL_MODELTYPES: ModelTypeMapping = {
  INCIDENT: "Incident",
  OBSERVATION: "Observation",
  INSPECTION: "Inspection",
  AUDITS: "Audit",
  CERTIFICATION: "Certification",
  REGULATION: "Regulation",
  ELIGIBILITY_PERMIT: "EligibilityPermit",
  COMPETENCE_PERMIT: "CompetencePermit",
  COMPANY_WIDE_PERMIT: "CompanyWidePermit",
  SITE_SPECIFIC_PERMIT: "SiteSpecificPermit",
  ASSET: "Asset",
};
export enum ModelType {
  OBSERVATION = "Observation",
  INCIDENT = "Incident",
  INSPECTION = "Inspection",
  COURSE = "Course",
  REGULATION = "Regulation",
  CERTIFICATION = "Certification",
  STOREST = "Store-transaction",
  STORES = "Store",
  RENEWALS = "Renewal",
  PERMITS = "Permit",
  AUDITS = "Audit",
  ACTIONS = "Action",
  ACTION_COMMENTS = "Action-comment",
  TRAININGS = "Training",
  FINDINGS = "Finding",
  COSTS = "Cost",
  EMPLOYEE = "Employee",
  USER = "User",
  APPROVAL = "Approval",
  GENERALINVENTORY = "GeneralInventory",
  HEALTHINVENTORY = "HealthInventory",
  SITE = "Site",
  ELIGIBILITY_PERMIT = "EligibilityPermit",
  COMPETENCE_PERMIT = "CompetencePermit",
  COMPANY_WIDE_PERMIT = "CompanyWidePermit",
  SITE_SPECIFIC_PERMIT = "SiteSpecificPermit",
  ELIGIBILITY_PERMIT_DOCUMENT = "EligibilityPermitDocument",
  COMPETENCE_PERMIT_DOCUMENT = "CompetencePermitDocument",
  COMPANY_WIDE_PERMIT_DOCUMENT = "CompanyWidePermitDocument",
  SITE_SPECIFIC_PERMIT_DOCUMENT = "SiteSpecificPermitDocument",
  ASSET = "Asset",
  MEETING_ENTRY = "MeetingEntry",
}
export const COLLECTION_TYPES = {
  ItemType: "ItemType",
  Severity: "Severity",
  Site: "Site",
  Status: "Status",
  WorkRelated: "WorkRelated",
  Employee: "Employee",
  Risk: "Risk",
  Location: "Location",
  InjuryCase: "InjuryCase",
  Illness: "Illness",
  Concern: "Concern",
  JobType: "JobType",
  DirectReport: "DirectReport",
  EmploymentType: "EmploymentType",
  Function: "Function",
  Concerns: "Concern",
  GoverningBody: "GoverningBody",
  FormType: "FormType",
  ActionStatus: "ActionStatus",
  CauseType: "CauseType",
  EffectType: "EffectType",
  GROUPBY: "GroupBy",
  Title: "Title",
  Gender: "Gender",
  Nationality: "Nationality",
  EnvironmentalDataPointType: "EnvironmentalDataPointType",
  EnvironmentalDataPoint: "EnvironmentalDataPoint",
  UnitOfMeasurement: "UnitOfMeasurement",
  InjuryClassification: "InjuryClassification",
  LostTimeType: "LostTimeType",
  AssetGroup: "AssetGroup",
  AssetCategory: "AssetCategory",
  Manufacturer: "Manufacturer",
  AssetModel: "AssetModel",
  ContributingFactor: "ContributingFactor",
};

export const ACTION_MODEL_TYPES = {
  Inspection: "Inspection",
  Audit: "Audit",
};

export const ANALYTICS_TYPES = {
  COURSE: "course",
  INCIDENT: "incident",
  INSPECTION: "inspection",
  OBSERVATION: "observation",
};

export const ENTRY_TYPES = {
  MEETING: "meeting",
  WASTE: "waste",
  HEALTH: "health",
  CONSUMPTION: "consumption",
} as const;

export const MEETING_ENTRY_TYPES = {
  MINUTES: "minutes",
  ATTACHMENTS: "attachments",
  ATTENDANCE: "attendance",
};

export const QUESTION_TYPES = {
  SHORT_ANSWER: "short_answer",
  PARAGRAPH: "paragraph",
  MULTIPLE_CHOICE: "multiple_choice",
  CHECKBOXES: "checkboxes",
  DROPDOWN: "dropdown",
  DATE: "date",
  TIME: "time",
  FILE_UPLOAD: "file_upload",
  DATE_RANGE: "daterange",
  RADIO: "radio",
  CHECKBOX: "checkbox",
  SIGNATURE: "signature",
};

export const FORM_BUILDER_CONFIG = {
  TEMPLATES: "Templates",
  QUESTIONS: "Questions",
};

export enum InventoryType {
  General = "general",
  Health = "health",
}
export const SETTINGS_SIDEBAR_TAGS = {
  FORM_BUILDER: "",
  TAGS: "tags",
  FORM_OPTIONS: "form-options",
  CHANGE_PASSWORD: "change-password",
  APPROVALS: "approvals",
  ALERTS: "alerts",
  CURRENCY_SETTINGS: "currency-settings",
};

// export const PermissionKeys = {
//   dashboard: {
//     viewActionsOnDashboard: "view_actions_on_dashboard",
//     viewItemsOnDashboard: "view_items_on_dashboard",
//     closeItemsOnDashboard: "close_items_on_dashboard",
//     viewFindingsOnActions: "view_findings_on_actions",
//     addFindingsToItemsFromActions: "add_findings_to_actions",
//   },
//   reports: {
//     viewDashboards: "view_dashboards",
//     createDashboards: "create_dashboards",
//     updateDashboards: "update_dashboards",
//     deleteDashboards: "delete_dashboards",
//     viewQueries: "view_queries",
//     createQueries: "create_queries",
//     updateQueries: "update_queries",
//     deleteQueries: "delete_queries",
//   },
//   itemManagement: {
//     incidents: {
//       view: "view_incidents",
//       create: "create_incidents",
//       edit: "edit_incidents",
//       delete: "delete_incidents",
//       viewFindings: "view_findings_on_incidents",
//       addFindings: "add_findings_to_incidents",
//       addCosts: "add_costs_to_incidents",
//       viewCosts: "view_costs_on_incidents",
//       editCosts: "edit_costs",
//       deleteCosts: "delete_costs",
//       addActions: "add_actions_to_incidents",
//     },
//     observations: {
//       view: "view_observations",
//       create: "create_observations",
//       edit: "edit_observations",
//       viewFindings: "view_findings_on_observations",
//       addFindings: "add_findings_to_observations",
//       addActions: "add_actions_to_observations",
//     },
//     inspections: {
//       view: "view_inspections",
//       create: "create_inspections",
//       respondAndSubmit: "respond_to_and_submit_inspections",
//       viewFindings: "view_findings_on_inspections",
//       addFindings: "add_findings_to_inspections",
//       addActions: "add_actions_to_inspections",
//     },
//     audits: {
//       view: "view_audits",
//       create: "create_audits",
//       respondAndSubmit: "respond_to_and_submit_audits",
//       viewFindings: "view_findings_on_audits",
//       addFindings: "add_findings_to_audits",
//       addActions: "add_actions_to_audits",
//     },
//   },
//   complianceManagement: {
//     certifications: {
//       view: "view_certifications",
//       create: "create_certifications",
//       update: "update_certifications",
//       viewRenewals: "view_renewals_on_certifications",
//       addRenewals: "add_renewals_to_certifications",
//       updateRenewals: "update_renewals",
//     },
//     regulations: {
//       view: "view_regulations",
//       create: "create_regulations",
//       update: "update_regulations",
//       viewPermits: "view_permits_on_regulations",
//       addPermits: "add_permits_to_regulations",
//       updatePermits: "update_permits",
//     },
//   },
//   resourceManagement: {
//     employees: {
//       view: "view_employees",
//       create: "create_employees",
//       update: "update_employees",
//       delete: "delete_employees",
//     },
//     rolesAndPermissions: {
//       view: "view_roles_and_permissions",
//       createAndAssign: "create_roles_and_assign_permissions",
//       editAndUpdate: "edit_roles_and_update_permissions",
//       delete: "delete_roles",
//     },
//   },
//   inventory: {
//     generalRecords: {
//       view: "view_general_inventory_records",
//       add: "add_general_inventory_records",
//       update: "update_general_inventory_records",
//       delete: "delete_general_inventory_records",
//     },
//     healthRecords: {
//       view: "view_health_inventory_records",
//       add: "add_health_inventory_records",
//       update: "update_health_inventory_records",
//       delete: "delete_health_inventory_records",
//     },
//   },
//   recordManagement: {
//     environmentalRecords: {
//       view: "view_environment_records",
//       add: "add_environment_records",
//       update: "update_environment_records",
//       delete: "delete_environment_records",
//     },
//     healthRecords: {
//       view: "view_health_records",
//       add: "add_health_records",
//       update: "update_health_records",
//       delete: "delete_health_records",
//     },
//     meetingRecords: {
//       view: "view_meeting_records",
//       add: "add_meeting_records",
//       update: "update_meeting_records",
//       delete: "delete_meeting_records",
//     },
//     manHourRecords: {
//       view: "view_manhour_records",
//       add: "add_manhour_records",
//       update: "update_manhour_records",
//       delete: "delete_manhour_records",
//     },
//   },
//   settings: {
//     questions: {
//       view: "view_questions",
//       add: "add_questions",
//       update: "update_questions",
//       delete: "delete_questions",
//     },
//     templates: {
//       view: "view_templates",
//       add: "add_templates",
//       update: "update_templates",
//       delete: "delete_templates",
//     },
//     formOptions: {
//       view: "view_form_options",
//       add: "add_form_options",
//       update: "update_form_options",
//       delete: "delete_form_options",
//     },
//   },
//   approvals: {
//     view: "view_approvals",
//     add: "add_approvals",
//     update: "update_approvals",
//     delete: "delete_approvals",
//   },
//   alerts: {
//     view: "view_alerts",
//     add: "add_alerts",
//     update: "update_alerts",
//     delete: "delete_alerts",
//   },
//   currency: {
//     setDefault: "set_default_currency",
//   },
// };
export const PermissionKeys = {
  dashboard: {
    viewActions: "view_actions_on_dashboard",
    viewItems: "view_items_on_dashboard",
    viewFindingsOnActions: "view_findings_on_actions",
    addFindingsToActions: "add_findings_to_actions",
    viewVoids: "view_voids",
  },
  reports: {
    viewDashboards: "view_dashboards",
    createDashboards: "create_dashboards",
    updateDashboards: "update_dashboards",
    deleteDashboards: "delete_dashboards",
    viewQueries: "view_queries",
    createQueries: "create_queries",
    updateQueries: "update_queries",
    deleteQueries: "delete_queries",
  },
  marketplace: {
    installTemplates: "install_marketplace_templates",
  },
  sites: {
    viewProjectSites: "view_project_sites",
  },
  itemManagement: {
    incidents: {
      view: "view_incidents",
      create: "create_incidents",
      edit: "edit_incidents",
      delete: "delete_incidents",
      viewFindings: "view_findings_on_incidents",
      addFindings: "add_findings_to_incidents",
      addCosts: "add_costs_to_incidents",
      viewCosts: "view_costs_on_incidents",
      editCosts: "edit_costs",
      deleteCosts: "delete_costs",
      addActions: "add_actions_to_incidents",
    },
    observations: {
      view: "view_observations",
      create: "create_observations",
      edit: "edit_observations",
      delete: "delete_observations",
      viewFindings: "view_findings_on_observations",
      addFindings: "add_findings_to_observations",
      addActions: "add_actions_to_observations",
    },
    inspections: {
      view: "view_inspections",
      create: "create_inspections",
      delete: "delete_inspections",
      respondAndSubmit: "respond_to_and_submit_inspections",
      viewFindings: "view_findings_on_inspections",
      addFindings: "add_findings_to_inspections",
      addActions: "add_actions_to_inspections",
    },
    audits: {
      view: "view_audits",
      create: "create_audits",
      delete: "delete_audits",
      respondAndSubmit: "respond_to_and_submit_audits",
      viewFindings: "view_findings_on_audits",
      addFindings: "add_findings_to_audits",
      addActions: "add_actions_to_audits",
    },
  },
  dailyOperations: {
    jsa: {
      view: "view_jsa",
      create: "create_jsa",
      edit: "edit_jsa",
      delete: "delete_jsa",
    },
    dra: {
      view: "view_dra",
      create: "create_dra",
      edit: "edit_dra",
      delete: "delete_dra",
    },
    ptw: {
      view: "view_ptw",
      create: "create_ptw",
      edit: "edit_ptw",
      delete: "delete_ptw",
    },
    coshh: {
      view: "view_coshh",
      create: "create_coshh",
      edit: "edit_coshh",
      delete: "delete_coshh",
    },
    liftingOperations: {
      view: "view_lifting_operations",
      create: "create_lifting_operations",
      edit: "edit_lifting_operations",
      delete: "delete_lifting_operations",
    },
  },
  complianceManagement: {
    eligibilityPermits: {
      view: "view_eligibility_permits",
      create: "create_eligibility_permits",
      update: "update_eligibility_permits",
      delete: "disable_eligibility_permits",
      viewRenewals: "view_documents_on_eligibility_permits",
      addRenewals: "add_documents_to_eligibility_permits",
      updateRenewals: "update_eligibility_permit_documents",
      deleteRenewals: "delete_eligibility_permit_documents",
    },
    competencePermits: {
      view: "view_competence_permits",
      create: "create_competence_permits",
      update: "update_competence_permits",
      delete: "disable_competence_permits",
      viewRenewals: "view_documents_on_competence_permits",
      addRenewals: "add_documents_to_competence_permits",
      updateRenewals: "update_competence_permit_documents",
      deleteRenewals: "delete_competence_permit_documents",
    },
    companyWidePermits: {
      view: "view_company_wide_permits",
      create: "create_company_wide_permits",
      update: "update_company_wide_permits",
      delete: "disable_company_wide_permits",
      viewPermits: "view_documents_on_company_wide_permits",
      addPermits: "add_documents_to_company_wide_permits",
      updatePermits: "update_company_wide_permit_documents",
      deletePermits: "delete_company_wide_permit_documents",
    },
    siteSpecificPermits: {
      view: "view_site_specific_permits",
      create: "create_site_specific_permits",
      update: "update_site_specific_permits",
      delete: "disable_site_specific_permits",
      viewPermits: "view_documents_on_site_specific_permits",
      addPermits: "add_documents_to_site_specific_permits",
      updatePermits: "update_site_specific_permit_documents",
      deletePermits: "delete_site_specific_permit_documents",
    },
  },
  resourceManagement: {
    employees: {
      view: "view_employees",
      create: "create_employees",
      update: "update_employees",
      delete: "delete_employees",
    },
    rolesAndPermissions: {
      view: "view_roles_and_permissions",
      createAndAssign: "create_roles_and_assign_permissions",
      editAndUpdate: "edit_roles_and_update_permissions",
      delete: "delete_roles",
    },
  },
  assetManagement: {
    assets: {
      view: "view_assets",
      create: "create_assets",
      edit: "edit_assets",
      delete: "delete_assets",
    },
  },
  inventory: {
    generalRecords: {
      view: "view_general_inventory_records",
      add: "add_general_inventory_records",
      update: "update_general_inventory_records",
      delete: "delete_general_inventory_records",
    },
    healthRecords: {
      view: "view_health_inventory_records",
      add: "add_health_inventory_records",
      update: "update_health_inventory_records",
      delete: "delete_health_inventory_records",
    },
  },
  recordManagement: {
    environmentalRecords: {
      view: "view_environment_records",
      add: "add_environment_records",
      update: "update_environment_records",
      delete: "delete_environment_records",
    },
    healthRecords: {
      view: "view_health_records",
      add: "add_health_records",
      update: "update_health_records",
      delete: "delete_health_records",
    },
    meetingRecords: {
      view: "view_meeting_records",
      add: "add_meeting_records",
      update: "update_meeting_records",
      delete: "delete_meeting_records",
    },
    manHourRecords: {
      view: "view_manhour_records",
      add: "add_manhour_records",
      update: "update_manhour_records",
      delete: "delete_manhour_records",
    },
  },
  settings: {
    questions: {
      view: "view_questions",
      add: "add_questions",
      update: "update_questions",
      delete: "delete_questions",
    },
    templates: {
      view: "view_templates",
      add: "add_templates",
      update: "update_templates",
      delete: "delete_templates",
    },
    formOptions: {
      view: "view_form_options",
      add: "add_form_options",
      update: "update_form_options",
      delete: "delete_form_options",
    },
  },
  approvals: {
    view: "view_approvals",
    add: "add_approvals",
    update: "update_approvals",
    delete: "delete_approvals",
  },
  alerts: {
    view: "view_alerts",
    add: "add_alerts",
    update: "update_alerts",
    delete: "delete_alerts",
  },
  currency: {
    setDefault: "set_default_currency",
  },
  riskMatrix: {
    view: "view_risk_matrix",
    add: "add_risk_matrix",
    update: "update_risk_matrix",
    delete: "delete_risk_matrix",
  },
};
export const WIDGETS = {
  ALL: "All",
  PIE_CHART: "Pie Chart",
  BAR_CHART: "Bar",
  COLUMN_CHART: "Column",
  PIVOT_TABLE: "Table",
  LINE_CHART: "Line",
  AREA_CHART: "Area",
  QUERY_TILE: "Query Tile",
  DOUGHNUT: "Doughnuts",
  SMART_WIDGET: "Smart Widget",
};

export const WIDGET_CHARTS = {
  EXPLODED_DOUGHNUT: "Exploded Doughnut",
  STANDARD_DOUGHNUT: "Standard Doughnut",
  QUERY_TILE: "Query Tile",
  STACKED_AREA: "Stacked Area",
  STANDARD_AREA: "Standard Area",
  SMOOTHED_LINE: "Smoothed Line",
  LINE_WITH_MARKERS: "Line with Markers",
  STACKED_LINE: "Stacked Line",
  STANDARD_LINE: "Standard Line",
  PIVOT_TABLE: "Pivot Table",
  CLUSTERED_COLUMN: "Clustered Column",
  STACKED_COLUMN: "Stacked Column",
  STANDARD_COLUMN: "Standard Column",
  CLUSTERED_BAR: "Clustered Bar",
  STACKED_BAR: "Stacked Bar",
  STANDARD_BAR: "Standard Bar",
  EXPLODED_PIE: "Exploded Pie",
  STANDARD_PIE: "Standard Pie",
  DAYS_SINCE_LAST: "Days Since Tile",
  PLACEHOLDER: "Placeholder Widget",
};

export const GROUP_BY_DAYS_SINCE_LAST = "Days Since Last";
export enum WidgetCatergoryTypes {
  ALL = "All",
  PIE_CHART = "Pie Chart",
  BAR_CHART = "Bar",
  COLUMN_CHART = "Column",
  PIVOT_TABLE = "Table",
  LINE_CHART = "Line",
  AREA_CHART = "Area",
  QUERY_TILE = "Query Tile",
  DOUGHNUT = "Doughnuts",
  SMART_WIDGET = "Smart Widget",
}

export enum WidgetChartTypes {
  BAR_CHART = "Bar Chart",
  LINE_CHART = "Line Chart",
  PIE_CHART = "Pie Chart",
  AREA_CHART = "Area Chart",
  EXPLODED_DOUGHNUT = "Exploded Doughnut",
  STANDARD_DOUGHNUT = "Standard Doughnut",
  QUERY_TILE = "Query Tile",
  STACKED_AREA = "Stacked Area",
  STANDARD_AREA = "Standard Area",
  SMOOTHED_LINE = "Smoothed Line",
  LINE_WITH_MARKERS = "Line with Markers",
  STACKED_LINE = "Stacked Line",
  STANDARD_LINE = "Standard Line",
  PIVOT_TABLE = "Pivot Table",
  CLUSTERED_COLUMN = "Clustered Column",
  STACKED_COLUMN = "Stacked Column",
  STANDARD_COLUMN = "Standard Column",
  CLUSTERED_BAR = "Clustered Bar",
  STACKED_BAR = "Stacked Bar",
  STANDARD_BAR = "Standard Bar",
  EXPLODED_PIE = "Exploded Pie",
  STANDARD_PIE = "Standard Pie",
  DAYS_SINCE_LAST = "Days Since Tile",
  PLACEHOLDER = "Placeholder Widget",
}

export enum PieChartTypes {
  EXPLODED_PIE = "Exploded Pie",
  STANDARD_PIE = "Standard Pie",
  EXPLODED_DOUGHNUT = "Exploded Doughnut",
  STANDARD_DOUGHNUT = "Standard Doughnut",
}
export const WIDGET_DIMENSIONS = [
  {
    label: "1",
    value: "1",
  },
  {
    label: "2",
    value: "2",
  },
  {
    label: "3",
    value: "3",
  },
  {
    label: "4",
    value: "4",
  },
  {
    label: "5",
    value: "5",
  },
  {
    label: "6",
    value: "6",
  },
  {
    label: "7",
    value: "7",
  },
  {
    label: "8",
    value: "8",
  },
];

export const QUERY_TILE_DIMENSIONS = [
  {
    label: "1",
    value: "1",
  },
  {
    label: "2",
    value: "2",
  },
  {
    label: "3",
    value: "3",
  },
  {
    label: "4",
    value: "4",
  },
];

// export const COLORS = ["#04202F", "#CBFF10", "#EF4444"];
export const COLORS = [
  "#FFEEE4",
  "#FFD6C3",
  "#FAF3DD",
  "#D5C6E0",
  "#ABC4AA",
  "#F4E8C1",
  "#E9D2A3",
  "#BFAF80",
  "#6B6054",
  "#3E3D32",
  "#FF6B6B",
  "#FFD93D",
  "#6BCB77",
  "#4D96FF",
  "#5F6CAF",
  "#2A6041",
  "#519872",
  "#D9CAB3",
  "#C49B66",
  "#6D3D14",
  "#FFBE0B",
  "#FB5607",
  "#FF006E",
  "#8338EC",
  "#3A86FF",
  "#1E1E2C",
  "#2A2A40",
  "#474973",
  "#8585A6",
  "#ECECEC",
];
export const QUERY_GROUPED_DROPDOWN_OPTIONS = [
  {
    label: "And",
    value: "1",
  },
  {
    label: "Or",
    value: "0",
  },
];

export const DATE_TIME = "System.DateTime";
export const DATE_TIME_NULLABLE = "System.Nullable`1[System.DateTime]";
export const SYSTEM_BOOLEAN = "System.Boolean";
export const SYSTEM_GUID = "System.Guid";
export const SYSTEM_GUID_NULLABLE = "System.Nullable`1[System.Guid]";
export const IS_NULL_OPERATOR = "is null";
export const IS_NOT_NULL_OPERATOR = "is not null";

export const QUERY_ITEM_TYPES = {
  INCIDENTS: "incidents",
  OBSERVATIONS: "observations",
  INSPECTIONS: "inspections",
  REGULATIONS: "regulations",
  PERMITS: "permits",
  ACTIONS: "actions",
  ACTONS_COMMENTS: "action-comments",
  AUDITS: "audits",
  CERTIFICATIONS: "certifications",
  RENEWALS: "renewals",
  FINDINGS: "findings",
  COSTS: "costs",
  ENVIRONMENT_RECORD: "environment-record",
};

export const QUERY_FIELDS_STORE = {
  incidents: [],
  observations: [],
  inspections: [],
  regulations: [],
  permits: [],
  actions: [],
  "action-comments": [],
  audits: [],
  certifications: [],
  renewals: [],
  findings: [],
  costs: [],
  "environment-record": [],
  Actions: [],
  Incidents: [],
  "Incident Sites": [],
  "Incident Types": [],
  Observations: [],
  "Observation Sites": [],
  "Observation Types": [],
  Inspections: [],
  "Inspection Sites": [],
  "Inspection Types": [],
  Audits: [],
  "Audit Sites": [],
  "Audit Types": [],
  Certifications: [],
  "Certification Types": [],
  Regulations: [],
  "Regulation Types": [],
  Permits: [],
  Renewals: [],
  "Environment Records": [],
  Users: [],
};

export const QUERY_OPERATORS_STORE = {
  incidents: [],
  observations: [],
  inspections: [],
  regulations: [],
  permits: [],
  actions: [],
  "action-comments": [],
  audits: [],
  certifications: [],
  renewals: [],
  findings: [],
  costs: [],
  "environment-record": [],
  Actions: [],
  Incidents: [],
  "Incident Sites": [],
  "Incident Types": [],
  Observations: [],
  "Observation Sites": [],
  "Observation Types": [],
  Inspections: [],
  "Inspection Sites": [],
  "Inspection Types": [],
  Audits: [],
  "Audit Sites": [],
  "Audit Types": [],
  Certifications: [],
  "Certification Types": [],
  Regulations: [],
  "Regulation Types": [],
  Permits: [],
  Renewals: [],
  "Environment Records": [],
  Users: [],
};

export const QUERY_VALUES_STORE = {
  incidents: [],
  observations: [],
  inspections: [],
  regulations: [],
  permits: [],
  actions: [],
  "action-comments": [],
  audits: [],
  certifications: [],
  renewals: [],
  findings: [],
  costs: [],
  "environment-record": [],
  Actions: [],
  Incidents: [],
  "Incident Sites": [],
  "Incident Types": [],
  Observations: [],
  "Observation Sites": [],
  "Observation Types": [],
  Inspections: [],
  "Inspection Sites": [],
  "Inspection Types": [],
  Audits: [],
  "Audit Sites": [],
  "Audit Types": [],
  Certifications: [],
  "Certification Types": [],
  Regulations: [],
  "Regulation Types": [],
  Permits: [],
  Renewals: [],
  "Environment Records": [],
  Users: [],
};

export const SINGLE_CLAUSE = "single_clause";
export const GROUPED_CLAUSE = "grouped_clause";

export const RECENT_SEARCHES = "recent_searches";

export const INJURY_CLASSIFICATION_LABELS = {
  NO_IMPACTED_DAYS: "No impacted days",
  FATALITY: "Fatality",
} as const;

export const INJURY_CLASSIFICATION_OPTIONS = {
  NoImpactedDays: {
    label: INJURY_CLASSIFICATION_LABELS.NO_IMPACTED_DAYS,
    value: "",
  },
  Fatality: { label: INJURY_CLASSIFICATION_LABELS.FATALITY, value: "11" },
} as const;

export const isNoImpactedDays = (classification: any) => {
  if (!classification) return false;
  return (
    classification.label === INJURY_CLASSIFICATION_LABELS.NO_IMPACTED_DAYS ||
    classification.value === INJURY_CLASSIFICATION_OPTIONS.NoImpactedDays.value
  );
};

export const isFatality = (classification: any) => {
  if (!classification) return false;
  return (
    classification.label === INJURY_CLASSIFICATION_LABELS.FATALITY ||
    classification.value === INJURY_CLASSIFICATION_OPTIONS.Fatality.value
  );
};

export const isMedicalTreatmentCase = (classification: any) => {
  if (!classification) return false;
  const label = classification.label?.toLowerCase() || "";
  const value = classification.value || "";
  return (
    label.includes("medical treatment case") ||
    label.includes("mtc") ||
    value === "a1b2c3d4-0003-0000-0000-000000000003"
  );
};

export const isFirstAidCase = (classification: any) => {
  if (!classification) return false;
  const label = classification.label?.toLowerCase() || "";
  const value = classification.value || "";
  return (
    label.includes("first aid case") ||
    label.includes("fac") ||
    value === "a1b2c3d4-0004-0000-0000-000000000004"
  );
};

export const isDaysFieldRequired = (classification: any) => {
  if (!classification || !classification.value) return false;
  return (
    !isNoImpactedDays(classification) &&
    !isFatality(classification) &&
    !isMedicalTreatmentCase(classification) &&
    !isFirstAidCase(classification)
  );
};
